/**
 * Following a list to its next page: the RFC 8288 `Link` header.
 *
 * Every list on the platform answers `{"items": [...]}` and says whether it
 * continues with `Link: <...>; rel="next"`. The target is the same path and
 * query with the continuation applied (`offset=N`, `page_token=...` or
 * `after=<seq>`, whichever the list uses), so a reader never builds the next
 * URL itself: it follows the one it was handed. No `rel="next"` means the page
 * is the last.
 *
 * This module is pure: it parses headers and maps a link target onto the
 * client's own path space. `api.getPage` and `api.getAllItems` (client.ts) are
 * the readers built on it.
 */

/** One link-value of a `Link` header: its target and its parameters. */
export interface LinkValue {
  target: string;
  /** Parameter names lowercased. The first occurrence of a name wins. */
  params: Record<string, string>;
}

const isWhitespace = (c: string | undefined) => c === " " || c === "\t";

/**
 * Parse a `Link` header field value (RFC 8288 section 3) into its link-values.
 *
 * Several header lines arrive joined with ", " (fetch combines them), and a
 * quoted parameter may itself contain commas and semicolons, so the value is
 * scanned rather than split. A link-value that does not start with `<` is
 * skipped up to the next top-level comma: one malformed value does not hide
 * the others.
 */
export function parseLinkHeader(header: string | null): LinkValue[] {
  if (!header) return [];
  const s = header;
  const n = s.length;
  const out: LinkValue[] = [];
  let i = 0;

  const skipWhitespace = () => {
    while (i < n && isWhitespace(s[i])) i++;
  };
  // Advance past the next comma that is not inside a quoted string.
  const skipToNextValue = () => {
    let quoted = false;
    while (i < n) {
      const c = s[i];
      if (quoted) {
        if (c === "\\") i++;
        else if (c === '"') quoted = false;
      } else if (c === '"') {
        quoted = true;
      } else if (c === ",") {
        i++;
        return;
      }
      i++;
    }
  };

  while (i < n) {
    while (i < n && (isWhitespace(s[i]) || s[i] === ",")) i++;
    if (i >= n) break;
    if (s[i] !== "<") {
      skipToNextValue();
      continue;
    }
    const close = s.indexOf(">", i + 1);
    if (close < 0) break;
    const target = s.slice(i + 1, close).trim();
    i = close + 1;
    const params: Record<string, string> = {};
    for (;;) {
      skipWhitespace();
      if (s[i] !== ";") break;
      i++;
      skipWhitespace();
      const nameStart = i;
      while (i < n && !/[=;,\s]/.test(s[i])) i++;
      const name = s.slice(nameStart, i).toLowerCase();
      skipWhitespace();
      let value = "";
      if (s[i] === "=") {
        i++;
        skipWhitespace();
        if (s[i] === '"') {
          i++;
          while (i < n && s[i] !== '"') {
            if (s[i] === "\\" && i + 1 < n) i++;
            value += s[i];
            i++;
          }
          i++; // closing quote
        } else {
          const valueStart = i;
          while (i < n && !/[;,\s]/.test(s[i])) i++;
          value = s.slice(valueStart, i);
        }
      }
      if (name && !(name in params)) params[name] = value;
    }
    out.push({ target, params });
    skipToNextValue();
  }
  return out;
}

/**
 * The target of the first link-value whose `rel` names `rel`. `rel` holds a
 * space-separated list of relation types, compared case-insensitively. A link
 * with an `anchor` is about another resource (RFC 8288 section 3.2) — e.g. the
 * next page of a list embedded in this response — so it is not this
 * response's link and is skipped.
 */
export function linkTarget(header: string | null, rel: string): string | null {
  const wanted = rel.toLowerCase();
  for (const link of parseLinkHeader(header)) {
    if ("anchor" in link.params) continue;
    const rels = (link.params.rel ?? "").toLowerCase().split(/\s+/);
    if (rels.includes(wanted)) return link.target;
  }
  return null;
}

/**
 * The path, relative to the client's base URL, of the page a response says
 * comes next — or null when the response is the last page.
 *
 * The link target is resolved against the URL the response came from (it is
 * root-relative, `/api/v1/...`), and then the base URL's path is taken off so
 * the client can prefix it again. `baseUrl` may be absolute (a sub-app calling
 * its parent host) or root-relative (`/api/v1`, same origin).
 *
 * A target outside the API base is not a page this client can request; it is
 * a server defect, and it is thrown rather than treated as the last page.
 */
export function nextPagePath(
  headers: Headers,
  responseUrl: string,
  baseUrl: string,
  origin: string,
): string | null {
  const target = linkTarget(headers.get("Link"), "next");
  if (target === null) return null;
  const base = new URL(baseUrl, origin);
  const resolved = new URL(target, responseUrl || base.href);
  const basePath = base.pathname.replace(/\/+$/, "");
  if (
    resolved.origin !== base.origin ||
    (resolved.pathname !== basePath && !resolved.pathname.startsWith(`${basePath}/`))
  ) {
    throw new Error(
      `Link rel="next" target ${target} is outside the API base ${base.href}`,
    );
  }
  return resolved.pathname.slice(basePath.length) + resolved.search;
}
